import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Avatar,
} from '@mui/material';
import {
  ArrowBack,
  AccountBalance as AccountIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { Account, AccountPosting, AccountTransaction } from '../types/account';
import { accountService } from '../services/api';
import BalanceDisplay from '../components/BalanceDisplay';
import PostingTable from '../components/PostingTable';
import {
  getAccountTypeLabel,
  getAccountTypeBadgeColor,
  getAccountStatusBadge,
  formatDate,
  isControlAccount,
} from '../utils/accountUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const AccountDetail: React.FC = () => {
  const { accountNumber } = useParams<{ accountNumber: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [account, setAccount] = useState<Account | null>(null);
  const [postings, setPostings] = useState<AccountPosting[]>([]);
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPostings, setLoadingPostings] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (accountNumber) {
      loadAccountDetails();
    }
  }, [accountNumber]);

  const loadAccountDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const accountData = await accountService.getAccount(accountNumber!);
      setAccount(accountData);

      // Load postings
      loadPostings();
    } catch (err: any) {
      console.error('Error loading account:', err);
      setError(err.response?.data?.message || 'Failed to load account details');
    } finally {
      setLoading(false);
    }
  };

  const loadPostings = async () => {
    try {
      setLoadingPostings(true);
      const response = await accountService.getAccountPostings(accountNumber!, {
        page: 0,
        size: 50,
      });
      setPostings(response.content);
    } catch (err: any) {
      console.error('Error loading postings:', err);
    } finally {
      setLoadingPostings(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !account) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Account not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/accounts')}>
          Back to Accounts
        </Button>
      </Box>
    );
  }

  const statusBadge = getAccountStatusBadge(account);

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Dashboard
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/accounts')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Accounts
        </Link>
        <Typography color="text.primary">{account.accountNumber}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/accounts')}>
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            <AccountIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">{account.accountName}</Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={getAccountTypeLabel(account.accountType)}
                color={getAccountTypeBadgeColor(account.accountType)}
                size="small"
              />
              <Chip label={statusBadge.label} color={statusBadge.color} size="small" />
              <Typography variant="caption" color="text.secondary">
                {account.accountNumber}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadAccountDetails}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<DownloadIcon />}>
            Export Statement
          </Button>
        </Box>
      </Box>

      {/* Balance Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <BalanceDisplay
                amount={account.currentBalance}
                currency={account.currency}
                label="Current Balance"
                size="large"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <BalanceDisplay
                amount={account.availableBalance}
                currency={account.currency}
                label="Available Balance"
                size="large"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Account Information
              </Typography>
              {account.partnerName ? (
                <Box>
                  <Typography variant="body2">
                    <strong>Partner:</strong>
                  </Typography>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate(`/partners/${account.partnerId}`)}
                  >
                    {account.partnerName}
                  </Link>
                </Box>
              ) : (
                <Typography variant="body2" color="primary" fontWeight="medium">
                  Control Account
                </Typography>
              )}
              <Typography variant="caption" display="block" mt={1}>
                Created: {formatDate(account.dateCreated)}
              </Typography>
              {account.canHaveNegativeBalance && (
                <Chip label="Credit Allowed" size="small" color="info" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Transaction History" />
          <Tab label="Posting Details" />
          <Tab label="Account Info" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Transaction History Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              Recent Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Transaction history feature will show grouped postings by transaction.
            </Typography>
          </TabPanel>

          {/* Postings Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              All Postings
            </Typography>
            {loadingPostings ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <PostingTable
                postings={postings}
                showAccount={false}
                showTransaction={true}
                dense={false}
              />
            )}
          </TabPanel>

          {/* Account Info Tab */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Account Number
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {account.accountNumber}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" mt={2}>
                  Account Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {account.accountName}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" mt={2}>
                  Account Type
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {getAccountTypeLabel(account.accountType)}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" mt={2}>
                  Currency
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {account.currency}
                </Typography>

                {account.partnerCode && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" mt={2}>
                      Partner Code
                    </Typography>
                    <Typography variant="body1" gutterBottom sx={{ fontFamily: 'monospace' }}>
                      {account.partnerCode}
                    </Typography>
                  </>
                )}

                {account.description && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" mt={2}>
                      Description
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {account.description}
                    </Typography>
                  </>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {account.isActive ? 'Active' : 'Inactive'}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" mt={2}>
                  Can Have Negative Balance
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {account.canHaveNegativeBalance ? 'Yes' : 'No'}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" mt={2}>
                  Date Created
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(account.dateCreated)}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" mt={2}>
                  Last Updated
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(account.lastUpdated)}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" mt={2}>
                  Control Account
                </Typography>
                <Box sx={{ mb: 1 }}>
                  {account.isControlAccount ? (
                    <Chip label="Control Account" size="small" color="info" />
                  ) : (
                    <Typography variant="body1">No</Typography>
                  )}
                </Box>

                {account.lastTransactionDate && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" mt={2}>
                      Last Transaction Date
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDate(account.lastTransactionDate)}
                    </Typography>
                  </>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" mt={2}>
                  Technical Details
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {account.id}
                </Typography>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default AccountDetail;
